"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useActionState, useTransition, Suspense, type FC } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./SellHero.module.css";
import Image from "next/image";
import { decodeVehicleAction } from "../actions";
import { FormStepper } from "./FormStepper";
import { useFormFlow } from "@/hooks/useFormFlow";
interface State {
  label: string;
  value: string;
}
interface HeroData {
  heading?: string;
  subheading?: string;
  formHeading?: string;
  licensePlaceholder?: string;
  statePlaceholder?: string;
  vinPlaceholder?: string;
  btnText?: string;
  trustBadges?: Array<{
    text: string;
    icon?: string;
  }>;
}
interface UnifiedModernHeroProps {
  heroData?: HeroData;
  states?: State[];
  backgroundImage: string;
}
export const UnifiedModernHero: FC<UnifiedModernHeroProps> = t0 => {
  const $ = _c(91);
  const {
    heroData: t1,
    states: t2,
    backgroundImage
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const heroData = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const states = t4;
  const [decodeState, decodeAction, isDecoding] = useActionState(decodeVehicleAction, null);
  const [isPending, startTransition] = useTransition();
  const [inputMethod, setInputMethod] = useState("plate");
  const [licensePlate, setLicensePlate] = useState("");
  const [vin, setVin] = useState("");
  const flow = useFormFlow("sell_my_car");
  let t5;
  if ($[4] !== decodeState?.errors || $[5] !== decodeState?.success || $[6] !== flow) {
    t5 = () => {
      if (decodeState?.success) {
        flow.complete("vehicle_lookup");
      } else {
        if (decodeState?.errors) {
          flow.error("lookup_failed", "vehicle_lookup");
        }
      }
    };
    $[4] = decodeState?.errors;
    $[5] = decodeState?.success;
    $[6] = flow;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== decodeState || $[9] !== flow) {
    t6 = [decodeState, flow];
    $[8] = decodeState;
    $[9] = flow;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (decodeState?.success && decodeState?.vehicleInfo) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="t-min-h-screen t-bg-ecommerce-background t-flex t-items-center t-justify-center"><div className="t-text-center t-space-y-4"><div className="t-animate-spin t-rounded-full t-h-12 t-w-12 t-border-b-2 t-border-automotive-primary t-mx-auto" /><div className="t-text-automotive-primary t-font-medium">Loading your appraisal form...</div></div></div>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== decodeState.vehicleInfo || $[13] !== flow) {
      t8 = <Suspense fallback={t7}><FormStepper vehicleInfo={decodeState.vehicleInfo} flow={flow} /></Suspense>;
      $[12] = decodeState.vehicleInfo;
      $[13] = flow;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  let t7;
  if ($[15] !== backgroundImage) {
    t7 = <Image src={backgroundImage} alt="THIScar branded vehicle hauler outside the Tomball facility" fill={true} priority={true} sizes="100vw" className={styles.heroPhoto} />;
    $[15] = backgroundImage;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className={styles.heroOverlay} />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t11;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0,
      y: 30
    };
    t10 = {
      opacity: 1,
      y: 0
    };
    t11 = {
      duration: 0.6
    };
    $[18] = t10;
    $[19] = t11;
    $[20] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t9 = $[20];
  }
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Image className={styles.buyingLogo} src="/media/sell/sell-us-your-car-logo-general.png" alt="THIScar.com Sell Us Your Car" width={390} height={125} priority={true} />;
    t13 = <p className={styles.eyebrow}>SELL US YOUR CAR</p>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  let t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <h1 className={styles.mainHeadline}>A real offer.<br />From a real appraiser.</h1>;
    t15 = <p className={styles.subheadline}>We’ll buy yours—even if you don’t buy one from us. No salespeople. No pressure. No problem.</p>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <motion.div initial={t9} animate={t10} transition={t11} className={styles.headlines}>{t12}{t13}{t14}{t15}<div className={styles.proof}>{["No purchase required", "We handle the paperwork", "Eight Greater Houston appraisal locations"].map(_temp)}</div></motion.div>;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  let t18;
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      opacity: 0,
      scale: 0.95
    };
    t18 = {
      opacity: 1,
      scale: 1
    };
    t19 = {
      duration: 0.5,
      delay: 0.2
    };
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t17 = $[26];
    t18 = $[27];
    t19 = $[28];
  }
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className={styles.formHeader}><small>START ONLINE</small><h2 className={styles.formTitle}>Let’s start with your car.</h2><p>Enter your license plate or VIN.</p></div>;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = `${styles.toggleButton} ${inputMethod === "plate" ? styles.active : ""}`;
  let t22;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setInputMethod("plate");
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  const t23 = inputMethod === "plate";
  let t24;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <svg className={styles.toggleIcon} viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M7 10h10M7 14h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] !== t21 || $[33] !== t23) {
    t25 = <button type="button" className={t21} onClick={t22} aria-pressed={t23}>{t24}License Plate</button>;
    $[32] = t21;
    $[33] = t23;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  const t26 = `${styles.toggleButton} ${inputMethod === "vin" ? styles.active : ""}`;
  let t27;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setInputMethod("vin");
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  const t28 = inputMethod === "vin";
  let t29;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <svg className={styles.toggleIcon} viewBox="0 0 24 24" fill="none"><path d="M7 7h10M7 12h10M7 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
    $[36] = t29;
  } else {
    t29 = $[36];
  }
  let t30;
  if ($[37] !== t26 || $[38] !== t28) {
    t30 = <button type="button" className={t26} onClick={t27} aria-pressed={t28}>{t29}VIN Number</button>;
    $[37] = t26;
    $[38] = t28;
    $[39] = t30;
  } else {
    t30 = $[39];
  }
  let t31;
  if ($[40] !== t25 || $[41] !== t30) {
    t31 = <div className={styles.inputToggle}>{t25}{t30}</div>;
    $[40] = t25;
    $[41] = t30;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  if ($[43] !== flow) {
    t32 = () => flow.begin("vehicle_lookup");
    $[43] = flow;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  if ($[45] !== flow) {
    t33 = e => {
      e.preventDefault();
      flow.submit("vehicle_lookup");
      const form = e.currentTarget;
      const formData = new FormData(form);
      startTransition(() => {
        decodeAction(formData);
      });
    };
    $[45] = flow;
    $[46] = t33;
  } else {
    t33 = $[46];
  }
  let t34;
  if ($[47] !== inputMethod) {
    t34 = <input type="hidden" name="inputMethod" value={inputMethod} />;
    $[47] = inputMethod;
    $[48] = t34;
  } else {
    t34 = $[48];
  }
  let t35;
  if ($[49] !== decodeState || $[50] !== heroData.licensePlaceholder || $[51] !== heroData.statePlaceholder || $[52] !== heroData.vinPlaceholder || $[53] !== inputMethod || $[54] !== licensePlate || $[55] !== states || $[56] !== vin) {
    t35 = <AnimatePresence mode="wait">{inputMethod === "plate" ? <motion.div key="plate" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 20
      }} transition={{
        duration: 0.3
      }} className={styles.inputGroup}><div className={styles.plateInputs}><div className={styles.inputWrapper}><input type="text" name="licensePlate" id="licensePlate" value={licensePlate} className={`${styles.input} ${decodeState?.errors?.licensePlate ? styles.error : ""}`} placeholder={heroData.licensePlaceholder || "Enter License Plate"} autoComplete="off" maxLength={8} onChange={e_0 => {
              setLicensePlate(e_0.target.value.toUpperCase());
            }} required={inputMethod === "plate"} aria-label="Vehicle License Plate Number" aria-describedby={decodeState?.errors?.licensePlate ? "licensePlate-error" : undefined} aria-invalid={decodeState?.errors?.licensePlate ? "true" : "false"} />{decodeState?.errors?.licensePlate && <div id="licensePlate-error" className={styles.errorMessage} role="alert" aria-live="polite">{decodeState.errors.licensePlate}</div>}</div><div className={styles.inputWrapper}><select name="state" id="state" className={`${styles.select} ${decodeState?.errors?.state ? styles.error : ""}`} required={inputMethod === "plate"} aria-label="Vehicle Registration State" aria-describedby={decodeState?.errors?.state ? "state-error" : undefined} aria-invalid={decodeState?.errors?.state ? "true" : "false"}><option value="">{heroData.statePlaceholder || "Select State"}</option>{states.map(_temp2)}</select>{decodeState?.errors?.state && <div id="state-error" className={styles.errorMessage} role="alert" aria-live="polite">{decodeState.errors.state}</div>}</div></div></motion.div> : <motion.div key="vin" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.3
      }} className={styles.inputGroup}><div className={styles.inputWrapper}><input type="text" name="vin" id="vin" value={vin} className={`${styles.input} ${decodeState?.errors?.vin ? styles.error : ""}`} placeholder={heroData.vinPlaceholder || "Enter VIN"} autoComplete="off" maxLength={17} pattern="[A-HJ-NPR-Z0-9]{17}" onChange={e_1 => {
            setVin(e_1.target.value.toUpperCase());
          }} required={inputMethod === "vin"} aria-label="Vehicle Identification Number (VIN)" aria-describedby={decodeState?.errors?.vin ? "vin-error" : "vin-help"} aria-invalid={decodeState?.errors?.vin ? "true" : "false"} />{decodeState?.errors?.vin && <div id="vin-error" className={styles.errorMessage} role="alert" aria-live="polite">{decodeState.errors.vin}</div>}<div id="vin-help" className={styles.helpText}>VIN is usually found on the driver's side dashboard</div></div></motion.div>}</AnimatePresence>;
    $[49] = decodeState;
    $[50] = heroData.licensePlaceholder;
    $[51] = heroData.statePlaceholder;
    $[52] = heroData.vinPlaceholder;
    $[53] = inputMethod;
    $[54] = licensePlate;
    $[55] = states;
    $[56] = vin;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== decodeState?.errors?.licensePlate || $[59] !== decodeState?.errors?.vin || $[60] !== inputMethod) {
    t36 = (inputMethod === "plate" && decodeState?.errors?.licensePlate || inputMethod === "vin" && decodeState?.errors?.vin) && <div className={styles.switchMethodContainer}><button type="button" onClick={() => setInputMethod(inputMethod === "plate" ? "vin" : "plate")} className={styles.switchMethod}>{inputMethod === "plate" ? "Use VIN instead?" : "Use License Plate instead?"}</button></div>;
    $[58] = decodeState?.errors?.licensePlate;
    $[59] = decodeState?.errors?.vin;
    $[60] = inputMethod;
    $[61] = t36;
  } else {
    t36 = $[61];
  }
  let t37;
  if ($[62] !== decodeState) {
    t37 = (decodeState?.errors?.general || decodeState?.errors?.duplicate) && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} className="t-mt-4 t-p-4 t-bg-brand-rose-soft t-border t-border-brand-rose-soft t-rounded-lg t-text-brand-rose-dark t-text-sm t-font-medium t-flex t-items-center t-gap-3"><svg className="t-h-5 t-w-5 t-flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.232 15.5c-.77.833.192 2.5 1.732 2.5z" /></svg><div>{decodeState.errors.duplicate && <div>{decodeState.errors.duplicate}</div>}{decodeState.errors.general && <div>{decodeState.errors.general}</div>}</div></motion.div>;
    $[62] = decodeState;
    $[63] = t37;
  } else {
    t37 = $[63];
  }
  let t38;
  if ($[64] !== t37) {
    t38 = <AnimatePresence>{t37}</AnimatePresence>;
    $[64] = t37;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  const t39 = isDecoding || isPending;
  const t40 = `${styles.submitButton} ${isDecoding || isPending ? "t-opacity-70 t-cursor-not-allowed" : ""}`;
  const t41 = isDecoding || isPending ? "Decoding vehicle information..." : "Get cash offer for your vehicle";
  let t42;
  if ($[66] !== heroData.btnText || $[67] !== isDecoding || $[68] !== isPending) {
    t42 = isDecoding || isPending ? <><svg className="t-w-5 t-h-5 t-animate-spin t-mr-2" viewBox="0 0 24 24"><circle className="t-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="t-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Decoding Vehicle...</> : <>{heroData.btnText || "Get Cash Offer"}<svg className="t-w-5 t-h-5 t-ml-2" viewBox="0 0 24 24" fill="none"><path d="M5 12h14m0 0l-7-7m7 7l-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></>;
    $[66] = heroData.btnText;
    $[67] = isDecoding;
    $[68] = isPending;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  let t43;
  if ($[70] !== t39 || $[71] !== t40 || $[72] !== t41 || $[73] !== t42) {
    t43 = <button type="submit" disabled={t39} className={t40} aria-describedby="submit-help" aria-label={t41}>{t42}</button>;
    $[70] = t39;
    $[71] = t40;
    $[72] = t41;
    $[73] = t42;
    $[74] = t43;
  } else {
    t43 = $[74];
  }
  let t44;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div id="submit-help" className="sr-only">Submit your vehicle information to get an instant cash offer. Your information is secure and confidential.</div>;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] !== t32 || $[77] !== t33 || $[78] !== t34 || $[79] !== t35 || $[80] !== t36 || $[81] !== t38 || $[82] !== t43) {
    t45 = <form name="sell_my_car_vehicle_lookup" onInput={t32} className={styles.form} noValidate={true} role="form" aria-label="Vehicle Information Form" onSubmit={t33}>{t34}{t35}{t36}{t38}{t43}{t44}</form>;
    $[76] = t32;
    $[77] = t33;
    $[78] = t34;
    $[79] = t35;
    $[80] = t36;
    $[81] = t38;
    $[82] = t43;
    $[83] = t45;
  } else {
    t45 = $[83];
  }
  let t46;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <p className={styles.offerNote}>Takes about two minutes. Your information stays private.</p>;
    $[84] = t46;
  } else {
    t46 = $[84];
  }
  let t47;
  if ($[85] !== t31 || $[86] !== t45) {
    t47 = <div className={styles.heroContainer}><div className={styles.heroContent}>{t16}<motion.div initial={t17} animate={t18} transition={t19} className={styles.formCard} id="offer">{t20}{t31}{t45}{t46}</motion.div></div></div>;
    $[85] = t31;
    $[86] = t45;
    $[87] = t47;
  } else {
    t47 = $[87];
  }
  let t48;
  if ($[88] !== t47 || $[89] !== t7) {
    t48 = <section id="sell-top" className={styles.heroSection} aria-label="Sell your car hero section">{t7}{t8}{t47}</section>;
    $[88] = t47;
    $[89] = t7;
    $[90] = t48;
  } else {
    t48 = $[90];
  }
  return t48;
};
function _temp(text) {
  return <span key={text}><span aria-hidden="true">✓</span>{text}</span>;
}
function _temp2(state) {
  return <option key={state.value} value={state.value}>{state.label}</option>;
}